// Bridge between the Theta Assist web app (this SPA) and the Chrome extension
// side panel that embeds it in an <iframe>. When embedded, read-only browser
// tool calls arrive over SignalR as `browser_read` thread events; we relay them
// up to the extension to run against the active tab, and the extension posts
// the observation back, which we send as a follow-up user message.
//
// A second, inline path serves the voice assistant (voice/VoiceBrowserActions):
// `requestFromExtension` posts a request with a locally generated callId and
// resolves a promise when the matching reply arrives. Every reply whose callId
// is pending here is "claimed": the broadcast subscribers below skip it, so a
// voice-originated read never turns into a thread message. The extension's
// `extension_hello` reply to `embed_ready` lists the bridge features the
// installed build supports; voice actions that need newer request types are
// offered only when the capability is present.
//
// A third path serves the text-chat browser-action tools (browser_snapshot,
// browser_act, browser_navigate, browser_tabs, browser_screenshot): the server
// publishes a `browser_action` thread event whose callId is reused as the bridge
// callId (`requestFromExtension` with an explicit `callId`), the reply is awaited
// inline and posted back to the server (hooks/useBrowserActionRelay). Because
// the callId is claimed while the request is pending, the broadcast subscribers
// leave its `screenshot_result` alone.
//
// The `source` discriminators and message shapes MUST stay in sync with
// GptAssistant.ExtensionUI/src/types.ts.
export const EMBED_SOURCE = 'theta-assist-embed'; // messages we send TO the extension
export const EXTENSION_SOURCE = 'theta-assist-extension'; // messages we receive FROM the extension

// Marker for the user message that carries a browser tool result back into the
// thread. The model reads the JSON; the UI renders it as a card.
export const BROWSER_RESULT_FENCE = 'browser-result';

const QUERY_FLAG = 'embed';
const QUERY_VALUE = 'extension';
const STORAGE_KEY = 'thetaAssist.embeddedInExtension';

function detectEmbedded(): boolean {
    let framed = false;
    try {
        framed = window.parent !== window;
    } catch {
        framed = true; // cross-origin access implies we are framed
    }
    if (!framed) {
        return false;
    }

    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get(QUERY_FLAG) === QUERY_VALUE) {
            // Persist so client-side navigation that drops the query string
            // (or an iframe reload) keeps embedded mode active.
            window.sessionStorage.setItem(STORAGE_KEY, 'true');
            return true;
        }
        return window.sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

const embedded = detectEmbedded();

export const isEmbeddedInExtension = (): boolean => embedded;

// Notify the parent extension that the embed loaded. The side panel uses this
// to confirm the iframe → panel postMessage channel is live (and to surface
// "iframe ready" + the href that was loaded), and replies with
// `extension_hello`. Fired once on module load; callers may re-post it when the
// hello has not arrived (for example the panel's listener was not yet attached).
export function postEmbedReady(): void {
    if (!embedded) {
        return;
    }
    try {
        window.parent.postMessage(
            {
                source: EMBED_SOURCE,
                type: 'embed_ready',
                href: window.location.href,
                at: Date.now()
            },
            '*'
        );
    } catch {
        // best-effort; only matters in embedded mode
    }
}

// ---------------------------------------------------------------------------
// Inline request/reply path (voice assistant) and extension capability handshake
// ---------------------------------------------------------------------------

// Any extension reply carrying a callId. Concrete shapes are narrowed by the
// typed wrappers further down.
export interface ExtensionReplyPayload {
    type: string;
    callId: string;
    ok?: boolean;
    error?: string;
    [key: string]: unknown;
}

interface PendingExtensionRequest {
    replyTypes: ReadonlySet<string>;
    resolve: (payload: ExtensionReplyPayload) => void;
    reject: (error: Error) => void;
    timer: number;
    // Epoch ms at which `timer` fires; kept so a deadline extension never
    // shortens the wait.
    deadline: number;
    onTimeout: () => void;
}

const pendingRequests = new Map<string, PendingExtensionRequest>();

// True while a locally awaited request owns this callId. The broadcast
// subscribers (thread relay, composer) use it to leave such replies alone.
export const isClaimedExtensionCallId = (callId: unknown): boolean =>
    typeof callId === 'string' && pendingRequests.has(callId);

export class ExtensionRequestTimeoutError extends Error {
    constructor(requestType: string) {
        super(
            `The browser extension did not answer the ${requestType} request.`
        );
        this.name = 'ExtensionRequestTimeoutError';
    }
}

export class ExtensionNotEmbeddedError extends Error {
    constructor() {
        super('Theta Assist is not running inside the browser extension.');
        this.name = 'ExtensionNotEmbeddedError';
    }
}

// Raised into a pending request's promise by abandonExtensionRequest (the
// server cancelled the run, so nobody wants the reply any more).
export class ExtensionRequestCancelledError extends Error {
    constructor(requestType: string) {
        super(`The ${requestType} request was cancelled.`);
        this.name = 'ExtensionRequestCancelledError';
    }
}

const generateCallId = (prefix: string): string => {
    const random =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    return `${prefix}-${random}`;
};

const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

/**
 * Post a request to the extension and resolve with the first reply of one of
 * `replyTypes` that carries the request's callId. Rejects with
 * ExtensionRequestTimeoutError when no reply arrives in time, which is also
 * how an older extension build that does not know the request shows up.
 *
 * The callId is generated locally unless `callId` is given; the browser-action
 * relay passes the server's callId so the extension's reply, the pending claim
 * and the server-side row all share one identifier. A caller-supplied id that is
 * already pending is rejected rather than double-registered.
 */
export function requestFromExtension<
    TReply extends ExtensionReplyPayload = ExtensionReplyPayload
>(
    request: {type: string} & Record<string, unknown>,
    options: {
        replyTypes: string[];
        timeoutMs?: number;
        callIdPrefix?: string;
        callId?: string;
    }
): Promise<TReply> {
    if (!embedded) {
        return Promise.reject(new ExtensionNotEmbeddedError());
    }
    const callId =
        options.callId ?? generateCallId(options.callIdPrefix ?? 'voice');
    if (pendingRequests.has(callId)) {
        return Promise.reject(
            new Error(`An extension request with callId ${callId} is pending.`)
        );
    }
    const timeoutMs = options.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
    return new Promise<TReply>((resolve, reject) => {
        const onTimeout = () => {
            pendingRequests.delete(callId);
            reject(new ExtensionRequestTimeoutError(request.type));
        };
        const timer = window.setTimeout(onTimeout, timeoutMs);
        pendingRequests.set(callId, {
            replyTypes: new Set(options.replyTypes),
            resolve: payload => resolve(payload as TReply),
            reject,
            timer,
            deadline: Date.now() + timeoutMs,
            onTimeout
        });
        try {
            window.parent.postMessage(
                {source: EMBED_SOURCE, ...request, callId},
                '*'
            );
        } catch (error) {
            window.clearTimeout(timer);
            pendingRequests.delete(callId);
            reject(error instanceof Error ? error : new Error(String(error)));
        }
    });
}

/**
 * Push a pending request's timeout out to `deadline` (epoch ms). Used when the
 * extension reports that an action is waiting on the user's Allow/Deny, which
 * can legitimately take longer than the action itself. Never shortens the wait.
 * Returns false when nothing is pending under that callId.
 */
export function extendExtensionRequestDeadline(
    callId: string,
    deadline: number
): boolean {
    const pending = pendingRequests.get(callId);
    if (!pending || deadline <= pending.deadline) {
        return false;
    }
    window.clearTimeout(pending.timer);
    pending.deadline = deadline;
    pending.timer = window.setTimeout(
        pending.onTimeout,
        Math.max(0, deadline - Date.now())
    );
    return true;
}

/**
 * Stop waiting for a pending request: releases the claim and rejects its promise
 * with ExtensionRequestCancelledError. A reply that arrives later is ignored by
 * the inline path (and, carrying a now-unclaimed callId, may reach the broadcast
 * subscribers, which is why the relay also tells the extension to cancel).
 */
export function abandonExtensionRequest(callId: string): boolean {
    const pending = pendingRequests.get(callId);
    if (!pending) {
        return false;
    }
    window.clearTimeout(pending.timer);
    pendingRequests.delete(callId);
    pending.reject(new ExtensionRequestCancelledError(callId));
    return true;
}

export interface ExtensionInfo {
    version: string;
    capabilities: ReadonlySet<string>;
}

let extensionInfo: ExtensionInfo | null = null;
const extensionInfoListeners = new Set<(info: ExtensionInfo | null) => void>();

// The installed extension's build info, or null until (and unless) it replied
// to `embed_ready`. Null means an older build without the handshake.
export const getExtensionInfo = (): ExtensionInfo | null => extensionInfo;

export const hasExtensionCapability = (name: string): boolean =>
    extensionInfo?.capabilities.has(name) ?? false;

export function subscribeToExtensionInfo(
    handler: (info: ExtensionInfo | null) => void
): () => void {
    extensionInfoListeners.add(handler);
    return () => {
        extensionInfoListeners.delete(handler);
    };
}

// The active browser tab beside the panel, pushed by the extension only while a
// voice session is open (see postVoiceSessionState).
export interface ActiveTabInfo {
    tabId?: number;
    url?: string;
    title?: string;
    restricted: boolean;
}

let lastActiveTabInfo: ActiveTabInfo | null = null;
const activeTabInfoListeners = new Set<(info: ActiveTabInfo | null) => void>();

export const getActiveTabInfo = (): ActiveTabInfo | null => lastActiveTabInfo;

export function subscribeToActiveTabInfo(
    handler: (info: ActiveTabInfo | null) => void
): () => void {
    activeTabInfoListeners.add(handler);
    return () => {
        activeTabInfoListeners.delete(handler);
    };
}

const setActiveTabInfo = (info: ActiveTabInfo | null) => {
    lastActiveTabInfo = info;
    activeTabInfoListeners.forEach(listener => listener(info));
};

// Tell the extension a voice session opened or closed. While open, the
// extension pushes `active_tab_info`; closing also drops the cached tab here so
// the voice prompt stops describing the tab once the session has ended.
export function postVoiceSessionState(active: boolean): void {
    if (!embedded) {
        return;
    }
    if (!active) {
        setActiveTabInfo(null);
    }
    window.parent.postMessage(
        {source: EMBED_SOURCE, type: 'voice_session_state', active},
        '*'
    );
}

// Record something the voice assistant did in the extension's diagnostics
// activity log. The panel sees its own bridge calls but not what the app did
// with the result, so a look at the page would otherwise log the capture and
// never the answer. Fire and forget; older builds ignore the message.
export function postVoiceActivityNote(note: {
    command: string;
    ok: boolean;
    detail?: string;
    error?: string;
    model?: string;
    tabId?: number;
}): void {
    if (!embedded) {
        return;
    }
    window.parent.postMessage(
        {source: EMBED_SOURCE, type: 'voice_activity_note', ...note},
        '*'
    );
}

// Ask the extension to open its microphone permission page in a full tab.
// Chrome does not show the microphone prompt inside the side panel (getUserMedia
// fails with "Permission dismissed"); once the extension origin holds the
// permission, this iframe inherits it through allow="microphone". Needs the
// `microphone_permission` capability; older builds ignore the message.
export function postMicrophonePermissionRequest(): void {
    if (!embedded) {
        return;
    }
    window.parent.postMessage(
        {source: EMBED_SOURCE, type: 'microphone_permission_request'},
        '*'
    );
}

// One module-level listener owns the inline path and the handshake. Installed
// at module load, before React mounts, so it runs ahead of every component
// subscriber in the same dispatch.
if (embedded) {
    window.addEventListener('message', (event: MessageEvent) => {
        const data = event.data as
            | (Partial<ExtensionReplyPayload> & {source?: string})
            | undefined;
        if (
            data?.source !== EXTENSION_SOURCE ||
            typeof data.type !== 'string'
        ) {
            return;
        }

        if (data.type === 'extension_hello') {
            const capabilities = Array.isArray(data.capabilities)
                ? (data.capabilities as unknown[]).filter(
                      (name): name is string => typeof name === 'string'
                  )
                : [];
            extensionInfo = {
                version: typeof data.version === 'string' ? data.version : '',
                capabilities: new Set(capabilities)
            };
            extensionInfoListeners.forEach(listener => listener(extensionInfo));
            return;
        }

        if (data.type === 'active_tab_info') {
            setActiveTabInfo({
                tabId: typeof data.tabId === 'number' ? data.tabId : undefined,
                url: typeof data.url === 'string' ? data.url : undefined,
                title: typeof data.title === 'string' ? data.title : undefined,
                restricted: data.restricted === true
            });
            return;
        }

        if (typeof data.callId !== 'string') {
            return;
        }
        const callId = data.callId;
        const pending = pendingRequests.get(callId);
        if (!pending || !pending.replyTypes.has(data.type)) {
            return;
        }
        window.clearTimeout(pending.timer);
        // Release the claim on a later task, not a microtask: a microtask
        // checkpoint runs between listeners of a browser-dispatched event, so the
        // broadcast subscribers would otherwise see the id as unclaimed.
        window.setTimeout(() => pendingRequests.delete(callId), 0);
        pending.resolve(data as ExtensionReplyPayload);
    });
}

postEmbedReady();

// A single read-only command in a (possibly batched) browser_read dispatch.
export interface BrowserReadCommand {
    command: string;
    reason?: string;
}

// One command's result inside a batched browser_read reply. Self-describing: it
// echoes the originating command + reason so the model and the UI can tell which
// read produced which value.
export interface BrowserReadResultEntry {
    command: string;
    reason?: string;
    ok: boolean;
    result?: unknown;
    error?: string;
}

// side panel -> SPA: the outcome of a browser tool dispatch, keyed by callId.
// The `browser_interaction_result` type name is the reply channel for every
// browser tool (today only browser_read) and is deliberately unchanged so the
// SPA and any installed extension build stay wire-compatible across releases.
export interface BrowserInteractionResultMessage {
    source: typeof EXTENSION_SOURCE;
    type: 'browser_interaction_result';
    callId: string;
    ok: boolean;
    result?: unknown;
    error?: string;
    // One entry per command in the batch (a single read produces a one-element
    // array). When set, prefer this over the top-level result/error, which are
    // used on their own only for whole-dispatch failures (e.g. a capture that
    // could not be delivered).
    results?: BrowserReadResultEntry[];
}

export interface BrowserInteractionResultPayload {
    callId: string;
    ok: boolean;
    result?: unknown;
    error?: string;
    results?: BrowserReadResultEntry[];
}

// Relay one or more read-only browser commands (from a SignalR thread event) up
// to the extension; it runs every command and posts all results back in one reply.
export function postBrowserRead(
    callId: string,
    reads: BrowserReadCommand[]
): void {
    if (!isEmbeddedInExtension()) {
        return;
    }
    window.parent.postMessage(
        {source: EMBED_SOURCE, type: 'browser_read', callId, reads},
        '*'
    );
}

// Ask the extension to navigate the ACTIVE browser tab to an external URL
// (user-initiated, from clicking a link in an assistant message). Reversible —
// not a mutation of page state.
export function postNavigateRequest(url: string): void {
    if (!isEmbeddedInExtension()) {
        return;
    }
    window.parent.postMessage(
        {source: EMBED_SOURCE, type: 'navigate_request', url},
        '*'
    );
}

// Ask the extension to capture the active tab. `full` captures the whole
// scrollable page (scroll-and-stitch). The image comes back via
// subscribeToScreenshotResult (no callId => attach to the composer).
export function postCaptureRequest(full = false): void {
    if (!isEmbeddedInExtension()) {
        return;
    }
    window.parent.postMessage(
        {source: EMBED_SOURCE, type: 'capture_request', full},
        '*'
    );
}

// The page-CSS-pixel rectangle a region screenshot was cropped to, plus the
// device-pixel `scale` it was rendered at and the frame it belongs to.
export interface ScreenshotRegion {
    x: number;
    y: number;
    width: number;
    height: number;
    scale: number;
    frame?: string;
}

export interface ScreenshotResultPayload {
    ok: boolean;
    dataUrl?: string;
    fileName?: string;
    // Set when this screenshot is a fallback (e.g. an encrypted document was
    // captured visually instead of attached) — shown beside the attachment.
    notice?: string;
    callId?: string;
    error?: string;
    // Present when the capture was cropped to an element or rectangle
    // (browser_screenshot with a region).
    region?: ScreenshotRegion;
}

// Subscribe to screenshot results from the extension. `callId` present =>
// model-initiated (`browser_read screenshot`); absent => user-initiated capture.
export function subscribeToScreenshotResult(
    handler: (payload: ScreenshotResultPayload) => void
): () => void {
    const onMessage = (event: MessageEvent) => {
        const data = event.data as
            | (Partial<ScreenshotResultPayload> & {
                  source?: string;
                  type?: string;
              })
            | undefined;
        if (
            data?.source !== EXTENSION_SOURCE ||
            data.type !== 'screenshot_result' ||
            isClaimedExtensionCallId(data.callId)
        ) {
            return;
        }
        handler({
            ok: Boolean(data.ok),
            dataUrl: data.dataUrl,
            fileName: data.fileName,
            notice: data.notice,
            callId: data.callId,
            error: data.error,
            region: data.region
        });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
}

// Convert a data URL (e.g. a screenshot PNG) into a File for the composer's
// attachment pipeline. Decoded in-memory rather than via fetch(): the SPA runs
// under the host page's CSP, and fetch('data:…') is blocked by connect-src.
export async function dataUrlToFile(
    dataUrl: string,
    fileName: string
): Promise<File> {
    const commaIndex = dataUrl.indexOf(',');
    const meta = dataUrl.slice(5, commaIndex); // strip leading "data:"
    const data = dataUrl.slice(commaIndex + 1);
    const isBase64 = /;base64/i.test(meta);
    const type = meta.replace(/;base64/i, '') || 'image/png';

    let bytes: Uint8Array;
    if (isBase64) {
        const binary = atob(data);
        bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
    } else {
        bytes = new TextEncoder().encode(decodeURIComponent(data));
    }

    return new File([bytes], fileName, {type});
}

// Ask the extension to convert the active tab to Markdown. The result comes back
// via subscribeToMarkdownResult (no callId => attach to the composer).
export function postMarkdownRequest(): void {
    if (!isEmbeddedInExtension()) {
        return;
    }
    window.parent.postMessage(
        {source: EMBED_SOURCE, type: 'markdown_request'},
        '*'
    );
}

export interface MarkdownResultPayload {
    ok: boolean;
    markdown?: string;
    fileName?: string;
    callId?: string;
    error?: string;
}

// Subscribe to page-markdown results from the extension. `callId` present =>
// model-initiated (`markdown/readable --attach`); absent => user-initiated.
export function subscribeToMarkdownResult(
    handler: (payload: MarkdownResultPayload) => void
): () => void {
    const onMessage = (event: MessageEvent) => {
        const data = event.data as
            | (Partial<MarkdownResultPayload> & {
                  source?: string;
                  type?: string;
              })
            | undefined;
        if (
            data?.source !== EXTENSION_SOURCE ||
            data.type !== 'markdown_result' ||
            isClaimedExtensionCallId(data.callId)
        ) {
            return;
        }
        handler({
            ok: Boolean(data.ok),
            markdown: data.markdown,
            fileName: data.fileName,
            callId: data.callId,
            error: data.error
        });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
}

// Wrap a markdown string as a .md File for the composer's attachment pipeline.
export function markdownToFile(markdown: string, fileName: string): File {
    return new File([markdown], fileName, {type: 'text/markdown'});
}

// Ask the extension to arm the in-page click-to-pick image picker. The chosen
// image comes back via subscribeToImageResult (no callId => attach to composer).
export function postImagePickRequest(): void {
    if (!isEmbeddedInExtension()) {
        return;
    }
    window.parent.postMessage(
        {source: EMBED_SOURCE, type: 'imagepick_request'},
        '*'
    );
}

export interface ImageResultPayload {
    ok: boolean;
    dataUrl?: string;
    fileName?: string;
    callId?: string;
    error?: string;
}

// Subscribe to page-image results from the extension. `callId` present =>
// model-initiated (`attachImage`); absent => user-initiated (click-to-pick).
export function subscribeToImageResult(
    handler: (payload: ImageResultPayload) => void
): () => void {
    const onMessage = (event: MessageEvent) => {
        const data = event.data as
            | (Partial<ImageResultPayload> & {
                  source?: string;
                  type?: string;
              })
            | undefined;
        if (
            data?.source !== EXTENSION_SOURCE ||
            data.type !== 'image_result' ||
            isClaimedExtensionCallId(data.callId)
        ) {
            return;
        }
        handler({
            ok: Boolean(data.ok),
            dataUrl: data.dataUrl,
            fileName: data.fileName,
            callId: data.callId,
            error: data.error
        });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
}

export interface FileResultPayload {
    ok: boolean;
    dataUrl?: string;
    fileName?: string;
    contentType?: string;
    // Batched delivery (attachSources): multiple files in one message.
    files?: {dataUrl: string; fileName: string; contentType?: string}[];
    callId?: string;
    error?: string;
}

// Subscribe to binary-file results from the extension (e.g. a SharePoint
// document fetched via REST). Always model-initiated (carries a callId): the
// file is uploaded and sent back as an attachment the backend extracts.
export function subscribeToFileResult(
    handler: (payload: FileResultPayload) => void
): () => void {
    const onMessage = (event: MessageEvent) => {
        const data = event.data as
            | (Partial<FileResultPayload> & {
                  source?: string;
                  type?: string;
              })
            | undefined;
        if (
            data?.source !== EXTENSION_SOURCE ||
            data.type !== 'file_result' ||
            isClaimedExtensionCallId(data.callId)
        ) {
            return;
        }
        handler({
            ok: Boolean(data.ok),
            dataUrl: data.dataUrl,
            fileName: data.fileName,
            contentType: data.contentType,
            files: data.files,
            callId: data.callId,
            error: data.error
        });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
}

// Subscribe to browser_interaction_result messages coming back from the extension.
export function subscribeToBrowserInteractionResults(
    handler: (payload: BrowserInteractionResultPayload) => void
): () => void {
    const onMessage = (event: MessageEvent) => {
        const data = event.data as Partial<BrowserInteractionResultMessage>;
        if (
            data?.source !== EXTENSION_SOURCE ||
            data.type !== 'browser_interaction_result' ||
            typeof data.callId !== 'string' ||
            isClaimedExtensionCallId(data.callId)
        ) {
            return;
        }
        handler({
            callId: data.callId,
            ok: Boolean(data.ok),
            result: data.result,
            error: data.error,
            results: data.results
        });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
}

// Wrap a browser tool result as the text of a user message. The fenced block
// keeps it machine-readable for the model and detectable for special rendering.
export function buildBrowserResultMessageText(
    payload: BrowserInteractionResultPayload
): string {
    return ['```' + BROWSER_RESULT_FENCE, JSON.stringify(payload), '```'].join(
        '\n'
    );
}

// Detect + parse a browser-result block out of a message's text (for rendering).
const BROWSER_RESULT_REGEX = new RegExp(
    '```' + BROWSER_RESULT_FENCE + '\\s*\\n([\\s\\S]*?)\\n```'
);

export function parseBrowserResultMessageText(
    text: string | undefined | null
): BrowserInteractionResultPayload | null {
    if (!text) {
        return null;
    }
    const match = text.match(BROWSER_RESULT_REGEX);
    if (!match) {
        return null;
    }
    try {
        const parsed = JSON.parse(match[1]) as BrowserInteractionResultPayload;
        if (typeof parsed?.callId !== 'string') {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

// ---------------------------------------------------------------------------
// Typed inline requests used by the voice assistant
// ---------------------------------------------------------------------------

// A `browser_read` reply. Inline reads answer on `browser_interaction_result`;
// a read that resolves to a document or capture (a SharePoint file, a
// screenshot, `--attach`) answers on its own channel instead, which callers
// treat as "this is a document, not readable text".
export type BrowserReadReply = ExtensionReplyPayload &
    Partial<BrowserInteractionResultPayload> & {
        type:
            | 'browser_interaction_result'
            | 'file_result'
            | 'markdown_result'
            | 'screenshot_result'
            | 'image_result';
        fileName?: string;
    };

export const requestBrowserRead = (
    reads: BrowserReadCommand[],
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS
): Promise<BrowserReadReply> =>
    requestFromExtension<BrowserReadReply>(
        {type: 'browser_read', reads},
        {
            replyTypes: [
                'browser_interaction_result',
                'file_result',
                'markdown_result',
                'screenshot_result',
                'image_result'
            ],
            timeoutMs
        }
    );

export type BrowserNavigateAction = 'url' | 'back' | 'forward' | 'reload';

export interface BrowserNavigateReply extends ExtensionReplyPayload {
    type: 'browser_navigate_result';
    ok: boolean;
    url?: string;
    title?: string;
    settled: boolean;
}

// Options shared by the requests the text-chat relay reuses: `tabId` addresses a
// tab other than the active one (needs the `browser_tabs_windows` capability;
// older builds ignore it and act on the active tab), `callId`/`timeoutMs` let
// the relay run the request under the server's identifier and deadline.
export interface BrowserRelayRequestOptions {
    tabId?: number;
    timeoutMs?: number;
    callId?: string;
}

export const requestBrowserNavigate = (
    action: BrowserNavigateAction,
    url?: string,
    {tabId, timeoutMs = 25_000, callId}: BrowserRelayRequestOptions = {}
): Promise<BrowserNavigateReply> =>
    requestFromExtension<BrowserNavigateReply>(
        {type: 'browser_navigate_request', action, url, tabId},
        {replyTypes: ['browser_navigate_result'], timeoutMs, callId}
    );

export type BrowserTabsAction = 'list' | 'switch' | 'open' | 'close';

export interface BrowserTabSummary {
    tabId: number;
    title: string;
    url: string;
    active: boolean;
    // Additive (browser_tabs_windows capability): all-windows listing.
    windowId?: number;
    windowType?: 'normal' | 'popup' | 'other';
    openerTabId?: number;
    focused?: boolean;
    // The extension's working tab for browser actions (see the plan's
    // working-tab session); marks where the next action lands by default.
    working?: boolean;
}

export interface BrowserTabsReply extends ExtensionReplyPayload {
    type: 'browser_tabs_result';
    ok: boolean;
    tabs?: BrowserTabSummary[];
    tab?: BrowserTabSummary;
    activeTabId?: number;
    candidates?: BrowserTabSummary[];
}

export const requestBrowserTabs = (
    action: BrowserTabsAction,
    {
        timeoutMs,
        callId,
        ...options
    }: {
        match?: string;
        url?: string;
        // open: a separate pop-up style window instead of a tab.
        asPopup?: boolean;
    } & BrowserRelayRequestOptions = {}
): Promise<BrowserTabsReply> =>
    requestFromExtension<BrowserTabsReply>(
        {type: 'browser_tabs_request', action, ...options},
        {
            replyTypes: ['browser_tabs_result'],
            timeoutMs: timeoutMs ?? (action === 'open' ? 25_000 : 5_000),
            callId
        }
    );

export type BrowserScrollTarget = 'top' | 'bottom' | 'up' | 'down' | 'text';

export interface BrowserScrollReply extends ExtensionReplyPayload {
    type: 'browser_scroll_result';
    ok: boolean;
    scrollY?: number;
    scrollHeight?: number;
    viewportHeight?: number;
    percent?: number;
    atTop?: boolean;
    atBottom?: boolean;
    foundText?: boolean;
}

export const requestBrowserScroll = (
    to: BrowserScrollTarget,
    amount?: number,
    text?: string
): Promise<BrowserScrollReply> =>
    requestFromExtension<BrowserScrollReply>(
        {type: 'browser_scroll_request', to, amount, text},
        {replyTypes: ['browser_scroll_result'], timeoutMs: 5_000}
    );

// ---------------------------------------------------------------------------
// Text-chat browser-action tools (browser_snapshot / browser_act /
// browser_screenshot ride `browser_action_request`; browser_navigate and
// browser_tabs reuse the voice request types above). Mirrors
// GptAssistant.ExtensionUI/src/types.ts; keep the two in step by hand.
// ---------------------------------------------------------------------------

// Advertised in `extension_hello` by builds that understand
// `browser_action_request`. Without it the relay answers the server with
// status 'unsupported' instead of waiting for a reply that never comes.
export const BROWSER_ACTION_CAPABILITY = 'browser_action';

// Advertised by builds whose `browser_tabs list` covers every window and whose
// navigate/act requests honour `tabId`. Informational for the relay: the
// requests are additive, so older builds simply act on the active tab.
export const BROWSER_TABS_WINDOWS_CAPABILITY = 'browser_tabs_windows';

// Closed set of actions the bundled page agent interprets. Nothing here is
// model-supplied code; the extension maps each name to its own implementation.
export type BrowserActionName =
    | 'snapshot'
    | 'click'
    | 'double_click'
    | 'right_click'
    | 'hover'
    | 'type'
    | 'press'
    | 'clear'
    | 'select'
    | 'check'
    | 'uncheck'
    | 'focus'
    | 'scroll'
    | 'scroll_into_view'
    | 'wait_for'
    | 'describe'
    | 'screenshot'
    | 'drag'
    | 'upload';

// How the page agent finds the element: a `ref` from an earlier snapshot, a
// CSS `selector`, or visible `text` (optionally narrowed by `role`); `frame`
// names the snapshot frame (`f2`) and `nth` picks among several matches.
export interface BrowserActionTarget {
    ref?: string;
    selector?: string;
    text?: string;
    role?: string;
    frame?: string;
    nth?: number;
}

// iframe -> side panel: run one browser action. `tabId` omitted means the
// extension's working tab.
export interface BrowserActionRequestMessage {
    source: typeof EMBED_SOURCE;
    type: 'browser_action_request';
    callId: string;
    action: BrowserActionName;
    target?: BrowserActionTarget;
    params?: Record<string, unknown>;
    tabId?: number;
    deadlineAt?: number;
}

// iframe -> side panel: the server gave up on this call (run cancelled or timed
// out); dismiss any confirmation prompt and stop working on it. Fire-and-forget;
// older builds ignore it.
export interface BrowserActionCancelMessage {
    source: typeof EMBED_SOURCE;
    type: 'browser_action_cancel';
    callId: string;
}

export type BrowserActionStatus =
    | 'ok'
    | 'error'
    | 'denied'
    | 'refused'
    | 'needs_update'
    | 'stale_ref'
    | 'ambiguous'
    | 'not_found'
    | 'not_visible'
    | 'disabled'
    | 'obstructed'
    | 'timeout'
    | 'restricted'
    | 'unsupported';

// side panel -> iframe: the action's outcome. `candidates` lists the matches when
// a target was `ambiguous`; `tabId`/`url`/`title` describe the tab acted on.
export interface BrowserActionResultMessage {
    source: typeof EXTENSION_SOURCE;
    type: 'browser_action_result';
    callId: string;
    ok: boolean;
    status: BrowserActionStatus;
    result?: unknown;
    error?: string;
    candidates?: string[];
    tabId?: number;
    url?: string;
    title?: string;
}

// side panel -> iframe: the action paused for the user's Allow/Deny in the side
// panel; the final result follows once they answer (or `denied` at `expiresAt`).
export interface BrowserActionPendingMessage {
    source: typeof EXTENSION_SOURCE;
    type: 'browser_action_pending';
    callId: string;
    status: 'awaiting_confirmation';
    summary: string;
    expiresAt: number;
}

export type BrowserActionResultReply = ExtensionReplyPayload &
    Omit<BrowserActionResultMessage, 'source'>;

// A screenshot action answers on the existing `screenshot_result` channel so the
// SPA's upload path is reused; the relay tells the two apart by `type`.
export type BrowserActionScreenshotReply = ExtensionReplyPayload &
    Omit<ScreenshotResultPayload, 'callId'> & {
        type: 'screenshot_result';
        callId: string;
    };

export type BrowserActionReply =
    | BrowserActionResultReply
    | BrowserActionScreenshotReply;

export interface BrowserActionRequest {
    action: BrowserActionName;
    target?: BrowserActionTarget;
    params?: Record<string, unknown>;
    tabId?: number;
    // Epoch ms after which the caller discards the result; the extension times
    // any Allow/Deny prompt out before it.
    deadlineAt?: number;
}

/**
 * Post a `browser_action_request` and resolve with its `browser_action_result`
 * (or `screenshot_result` for the screenshot action). `callId` should be the
 * server's callId so the reply, the pending claim and the server row all agree.
 */
export const requestBrowserAction = (
    {action, target, params, tabId, deadlineAt}: BrowserActionRequest,
    {
        timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
        callId
    }: {timeoutMs?: number; callId?: string} = {}
): Promise<BrowserActionReply> =>
    requestFromExtension<BrowserActionReply>(
        {
            type: 'browser_action_request',
            action,
            target,
            params,
            tabId,
            deadlineAt
        },
        {
            replyTypes: ['browser_action_result', 'screenshot_result'],
            timeoutMs,
            callId
        }
    );

// Tell the extension the server no longer wants this call's result.
export function postBrowserActionCancel(callId: string): void {
    if (!embedded) {
        return;
    }
    try {
        window.parent.postMessage(
            {source: EMBED_SOURCE, type: 'browser_action_cancel', callId},
            '*'
        );
    } catch {
        // best-effort; the extension times the prompt out on its own
    }
}

export interface BrowserActionPendingPayload {
    callId: string;
    summary: string;
    expiresAt: number;
}

// Subscribe to `browser_action_pending` notices. They are not replies (the
// request stays pending), so they bypass the inline path and reach every
// subscriber; the relay uses them to extend its wait.
export function subscribeToBrowserActionPending(
    handler: (payload: BrowserActionPendingPayload) => void
): () => void {
    const onMessage = (event: MessageEvent) => {
        const data = event.data as Partial<BrowserActionPendingMessage>;
        if (
            data?.source !== EXTENSION_SOURCE ||
            data.type !== 'browser_action_pending' ||
            typeof data.callId !== 'string'
        ) {
            return;
        }
        handler({
            callId: data.callId,
            summary: typeof data.summary === 'string' ? data.summary : '',
            expiresAt:
                typeof data.expiresAt === 'number' ? data.expiresAt : Date.now()
        });
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
}

export interface ComposerAttachmentOutcome {
    type: string;
    ok: boolean;
    fileName?: string;
    error?: string;
}

/**
 * Resolve with the next capture result of one of `types` that carries NO
 * callId, i.e. a composer attachment produced by postCaptureRequest /
 * postMarkdownRequest. Register the wait before posting the request. Rejects
 * with ExtensionRequestTimeoutError when nothing arrives in time.
 */
export function waitForComposerAttachment(
    types: string[],
    timeoutMs: number
): Promise<ComposerAttachmentOutcome> {
    if (!embedded) {
        return Promise.reject(new ExtensionNotEmbeddedError());
    }
    return new Promise<ComposerAttachmentOutcome>((resolve, reject) => {
        const cleanup = () => {
            window.clearTimeout(timer);
            window.removeEventListener('message', onMessage);
        };
        const onMessage = (event: MessageEvent) => {
            const data = event.data as
                | {
                      source?: string;
                      type?: string;
                      callId?: unknown;
                      ok?: unknown;
                      fileName?: unknown;
                      error?: unknown;
                  }
                | undefined;
            if (
                data?.source !== EXTENSION_SOURCE ||
                typeof data.type !== 'string' ||
                !types.includes(data.type) ||
                typeof data.callId === 'string'
            ) {
                return;
            }
            cleanup();
            resolve({
                type: data.type,
                ok: data.ok === true,
                fileName:
                    typeof data.fileName === 'string'
                        ? data.fileName
                        : undefined,
                error: typeof data.error === 'string' ? data.error : undefined
            });
        };
        const timer = window.setTimeout(() => {
            cleanup();
            reject(new ExtensionRequestTimeoutError(types.join('/')));
        }, timeoutMs);
        window.addEventListener('message', onMessage);
    });
}
